import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Shield, CheckCircle, AlertTriangle, Target, FileText, Users, TrendingUp,
  Plus, Loader2, Edit3, Trash2, Save, ClipboardCheck, BookOpen, Eye,
  BarChart3, Clock, XCircle, ArrowRight, CalendarClock, Play, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/compliance`;

const STATUS_COLORS = {
  compliant: { bg: '#34C759', label: 'Compliant' },
  partial: { bg: '#FF9500', label: 'Partial' },
  gap: { bg: '#FF3B30', label: 'Gap' },
  not_started: { bg: '#9CA3AF', label: 'Not Started' },
};

const CompliancePage = () => {
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [context, setContext] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [risks, setRisks] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingContext, setEditingContext] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(false);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [objDialogOpen, setObjDialogOpen] = useState(false);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [riskForm, setRiskForm] = useState({ type: 'risk', title: '', description: '', category: 'operational', likelihood: 'medium', impact: 'medium', owner: '', mitigation_plan: '', iso_clause: '' });
  const [objForm, setObjForm] = useState({ title: '', description: '', metric: '', target_value: '', current_value: '', unit: '%', owner: '', target_date: '', iso_clause: '' });
  const [docForm, setDocForm] = useState({ title: '', doc_type: 'policy', version: '1.0', owner: '', description: '', iso_clause: '' });
  const [reviewForm, setReviewForm] = useState({ date: '', attendees: '', summary: '', decisions: '', action_items: '', next_review_date: '' });
  const [saving, setSaving] = useState(false);
  const [autoEvaluating, setAutoEvaluating] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); fetchScheduleAndSnapshots(); }, []);

  const fetchScheduleAndSnapshots = async () => {
    try {
      const [sch, snp] = await Promise.all([
        axios.get(`${API}/schedule`, { headers }),
        axios.get(`${API}/snapshots?limit=12`, { headers }),
      ]);
      setSchedule(sch.data);
      setSnapshots(snp.data);
    } catch {}
  };

  const saveSchedule = async (patch) => {
    setScheduleSaving(true);
    try {
      const res = await axios.put(`${API}/schedule`, patch, { headers });
      setSchedule(res.data);
      toast.success('Schedule updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setScheduleSaving(false); }
  };

  const runScheduleNow = async () => {
    setRunningNow(true);
    try {
      const res = await axios.post(`${API}/schedule/run-now`, {}, { headers });
      const email = res.data.email || {};
      const emailNote = email.sent
        ? ` (digest emailed to ${email.recipients} HR/admin)`
        : email.error ? ` — email skipped: ${email.error.substring(0, 80)}` : '';
      toast.success(`Snapshot saved — compliance rate ${res.data.summary.compliance_rate}%${emailNote}` +
        (res.data.drop_alert_dispatched ? ' · drop alert dispatched' : ''));
      await fetchScheduleAndSnapshots();
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Run failed');
    } finally { setRunningNow(false); }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/schedule/send-digest`, {}, { headers });
      if (res.data.sent) {
        toast.success(`Digest emailed to ${res.data.recipients} recipient(s)`);
      } else {
        toast.warning(res.data.error ? `Email not sent: ${res.data.error.substring(0, 120)}` : 'Email not sent (Resend not configured?)');
      }
      await fetchScheduleAndSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSendingDigest(false); }
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/schedule/send-test-digest`, {}, { headers });
      if (res.data.sent) {
        toast.success(`Preview digest sent to ${res.data.recipient}`);
      } else {
        toast.warning(res.data.error
          ? `Preview not sent: ${res.data.error.substring(0, 160)}`
          : 'Preview could not be sent');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview send failed');
    } finally { setSendingTest(false); }
  };

  const fetchAll = async () => {
    try {
      const [dash, chk, ctx, pol, rsk, obj, doc] = await Promise.all([
        axios.get(`${API}/dashboard`, { headers }),
        axios.get(`${API}/checklist`, { headers }),
        axios.get(`${API}/context`, { headers }),
        axios.get(`${API}/policy`, { headers }),
        axios.get(`${API}/risks`, { headers }),
        axios.get(`${API}/objectives`, { headers }),
        axios.get(`${API}/documents`, { headers }),
      ]);
      setDashboard(dash.data); setChecklist(chk.data); setContext(ctx.data);
      setPolicy(pol.data); setRisks(rsk.data); setObjectives(obj.data); setDocuments(doc.data);
    } catch {} finally { setLoading(false); }
  };

  const saveContext = async () => {
    setSaving(true);
    try { const res = await axios.put(`${API}/context`, context, { headers }); setContext(res.data); setEditingContext(false); toast.success('Context saved'); fetchAll(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const savePolicy = async () => {
    setSaving(true);
    try { const res = await axios.put(`${API}/policy`, policy, { headers }); setPolicy(res.data); setEditingPolicy(false); toast.success('Policy saved'); fetchAll(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const createRisk = async () => {
    try { await axios.post(`${API}/risks`, riskForm, { headers }); toast.success('Risk/opportunity added'); setRiskDialogOpen(false); setRiskForm({ type: 'risk', title: '', description: '', category: 'operational', likelihood: 'medium', impact: 'medium', owner: '', mitigation_plan: '', iso_clause: '' }); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateRiskStatus = async (id, status) => {
    try { await axios.put(`${API}/risks/${id}`, { status }, { headers }); fetchAll(); } catch {}
  };

  const deleteRisk = async (id) => {
    try { await axios.delete(`${API}/risks/${id}`, { headers }); fetchAll(); } catch {}
  };

  const createObjective = async () => {
    try { await axios.post(`${API}/objectives`, objForm, { headers }); toast.success('Objective created'); setObjDialogOpen(false); setObjForm({ title: '', description: '', metric: '', target_value: '', current_value: '', unit: '%', owner: '', target_date: '', iso_clause: '' }); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateObjectiveStatus = async (id, status) => {
    try { await axios.put(`${API}/objectives/${id}`, { status }, { headers }); fetchAll(); } catch {}
  };

  const createDocument = async () => {
    try { await axios.post(`${API}/documents`, docForm, { headers }); toast.success('Document registered'); setDocDialogOpen(false); setDocForm({ title: '', doc_type: 'policy', version: '1.0', owner: '', description: '', iso_clause: '' }); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addReview = async () => {
    try { await axios.post(`${API}/policy/management-review`, reviewForm, { headers }); toast.success('Management review recorded'); setReviewDialogOpen(false); setReviewForm({ date: '', attendees: '', summary: '', decisions: '', action_items: '', next_review_date: '' }); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const updateChecklistItem = async (clause, status) => {
    try { await axios.put(`${API}/checklist/${clause}`, { status }, { headers }); fetchAll(); } catch {}
  };

  const autoEvaluate = async () => {
    setAutoEvaluating(true);
    try {
      const res = await axios.post(`${API}/checklist/auto-evaluate`, {}, { headers });
      toast.success(res.data.message);
      setChecklist(res.data.checklist);
      setDashboard(prev => ({ ...prev, checklist_summary: res.data.checklist.summary }));
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-evaluation failed'); }
    finally { setAutoEvaluating(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;

  const ds = dashboard || {};
  const cs = ds.checklist_summary || {};

  return (
    <div data-testid="compliance-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A] flex items-center gap-2"><Shield className="w-6 h-6 text-[#002FA7]" />ISO 30401:2018 Compliance Center</h1>
          <p className="text-sm text-[#4B5563]">Knowledge Management Systems — Governance, Policy, Risks, Objectives & Audit</p>
        </div>
        <Badge className="text-sm px-3 py-1" style={{ backgroundColor: cs.compliance_rate >= 80 ? '#34C75915' : cs.compliance_rate >= 50 ? '#FF950015' : '#FF3B3015', color: cs.compliance_rate >= 80 ? '#34C759' : cs.compliance_rate >= 50 ? '#FF9500' : '#FF3B30' }} data-testid="compliance-rate-badge">
          {cs.compliance_rate || 0}% Compliant
        </Badge>
      </div>

      {/* Dashboard Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="compliance-dashboard">
        {[
          { label: 'Clauses Compliant', value: `${cs.compliant || 0}/${cs.total || 0}`, icon: CheckCircle, color: '#34C759' },
          { label: 'Partial / Gap', value: `${cs.partial || 0} / ${cs.gap || 0}`, icon: AlertTriangle, color: '#FF9500' },
          { label: 'Open Risks', value: ds.risks?.open || 0, icon: Target, color: ds.risks?.high_risk > 0 ? '#FF3B30' : '#007AFF' },
          { label: 'Active Objectives', value: ds.objectives?.active || 0, icon: TrendingUp, color: '#002FA7' },
          { label: 'Documents', value: `${ds.documents?.approved || 0} approved`, icon: FileText, color: '#34C759' },
          { label: 'Mgmt Reviews', value: ds.governance?.management_reviews || 0, icon: Users, color: '#AF52DE' },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i} className="border-[#E5E7EB]" data-testid={`dashboard-card-${i}`}>
              <CardContent className="p-3 text-center">
                <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-[9px] text-[#9CA3AF]">{s.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Scheduled Weekly Auto-Evaluation */}
      <Card className="border-[#E5E7EB]" data-testid="schedule-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[#002FA7]" />Weekly Auto-Evaluation
              {schedule?.enabled ? (
                <Badge className="text-[9px] bg-[#34C759]/15 text-[#34C759] ml-1">Active</Badge>
              ) : (
                <Badge className="text-[9px] bg-[#9CA3AF]/15 text-[#9CA3AF] ml-1">Paused</Badge>
              )}
              {schedule?.email_enabled && (
                <Badge className="text-[9px] bg-[#002FA7]/10 text-[#002FA7] ml-1 flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#002FA7]" />Email digest on
                </Badge>
              )}
            </CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-[11px] text-[#4B5563]" onClick={sendTestDigest} disabled={sendingTest} data-testid="send-test-digest-btn" title="Send a one-off preview to your own email">
                {sendingTest ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                Test to me
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={sendDigestNow} disabled={sendingDigest} data-testid="send-digest-btn">
                {sendingDigest ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                Send Digest
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={runScheduleNow} disabled={runningNow} data-testid="run-schedule-now-btn">
                {runningNow ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                Run Now
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-[10px] text-[#4B5563]">Cron</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="checkbox"
                  checked={!!schedule?.enabled}
                  onChange={e => saveSchedule({ enabled: e.target.checked })}
                  disabled={scheduleSaving}
                  className="w-4 h-4 accent-[#002FA7]"
                  data-testid="schedule-enabled-toggle"
                />
                <span className="text-xs text-[#0A0A0A]">Weekly</span>
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-[#4B5563]">Email digest</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="checkbox"
                  checked={!!schedule?.email_enabled}
                  onChange={e => saveSchedule({ email_enabled: e.target.checked })}
                  disabled={scheduleSaving}
                  className="w-4 h-4 accent-[#002FA7]"
                  data-testid="schedule-email-toggle"
                />
                <span className="text-xs text-[#0A0A0A]">HR + admins</span>
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-[#4B5563]">Day of week</Label>
              <Select value={schedule?.day_of_week || 'mon'} onValueChange={v => saveSchedule({ day_of_week: v })}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="schedule-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday']].map(([v, l]) => (
                    <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-[#4B5563]">Hour (UTC)</Label>
              <Select value={String(schedule?.hour ?? 9)} onValueChange={v => saveSchedule({ hour: parseInt(v, 10) })}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="schedule-hour-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, h) => (
                    <SelectItem key={h} value={String(h)} className="text-xs">{String(h).padStart(2, '0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-[10px] text-[#4B5563] space-y-0.5">
              <p data-testid="schedule-last-run">
                <span className="text-[#9CA3AF]">Last run:</span>{' '}
                {schedule?.last_run_at ? `${format(new Date(schedule.last_run_at), 'MMM d, HH:mm')} — ${schedule.last_run_summary?.compliance_rate ?? '?'}%` : '—'}
              </p>
              <p data-testid="schedule-next-run">
                <span className="text-[#9CA3AF]">Next run:</span>{' '}
                {schedule?.enabled && schedule?.next_run_at ? format(new Date(schedule.next_run_at), 'MMM d, HH:mm \'UTC\'') : 'Paused'}
              </p>
              {schedule?.last_email_sent_at && (
                <p data-testid="schedule-last-email">
                  <span className="text-[#9CA3AF]">Last email:</span>{' '}
                  {format(new Date(schedule.last_email_sent_at), 'MMM d, HH:mm')} → {schedule.last_email_recipients || 0}
                </p>
              )}
            </div>
          </div>

          {/* Trend line */}
          {snapshots.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-[#4B5563] font-medium">Compliance Trend ({snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''})</p>
                <span className="text-[10px] text-[#9CA3AF]">
                  {snapshots[0]?.compliance_rate}% → <span className="font-semibold text-[#0A0A0A]">{snapshots[snapshots.length - 1]?.compliance_rate}%</span>
                </span>
              </div>
              <div className="flex items-end gap-1 h-14" data-testid="schedule-trend">
                {snapshots.map((s, i) => {
                  const color = s.compliance_rate >= 80 ? '#34C759' : s.compliance_rate >= 50 ? '#FF9500' : '#FF3B30';
                  return (
                    <div key={s.snapshot_id || i} className="flex-1 flex flex-col items-center gap-0.5" title={`${format(new Date(s.created_at), 'MMM d HH:mm')} — ${s.compliance_rate}% (${s.triggered_by})`}>
                      <div className="w-full rounded-t" style={{ height: `${Math.max(s.compliance_rate, 4)}%`, backgroundColor: color, minHeight: '4px' }} />
                      <span className="text-[7px] text-[#9CA3AF]">{s.compliance_rate}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="checklist">
        <TabsList className="w-full justify-start flex-wrap">
          <TabsTrigger value="checklist" data-testid="tab-checklist"><ClipboardCheck className="w-3.5 h-3.5 mr-1" />Compliance Checklist</TabsTrigger>
          <TabsTrigger value="context" data-testid="tab-context"><BookOpen className="w-3.5 h-3.5 mr-1" />Context & Scope</TabsTrigger>
          <TabsTrigger value="policy" data-testid="tab-policy"><Shield className="w-3.5 h-3.5 mr-1" />Policy & Leadership</TabsTrigger>
          <TabsTrigger value="risks" data-testid="tab-risks"><AlertTriangle className="w-3.5 h-3.5 mr-1" />Risk Register</TabsTrigger>
          <TabsTrigger value="objectives" data-testid="tab-objectives"><Target className="w-3.5 h-3.5 mr-1" />KM Objectives</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-3.5 h-3.5 mr-1" />Document Control</TabsTrigger>
        </TabsList>

        {/* ==================== COMPLIANCE CHECKLIST ==================== */}
        <TabsContent value="checklist" className="space-y-2" data-testid="checklist-content">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-[#4B5563]">Map each ISO 30401 clause to KStreak features and track compliance status</p>
            <div className="flex items-center gap-3">
              <Button size="sm" className="bg-[#34C759] hover:bg-[#2DA647] text-xs" onClick={autoEvaluate} disabled={autoEvaluating} data-testid="auto-evaluate-btn">
                {autoEvaluating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <BarChart3 className="w-3.5 h-3.5 mr-1" />}
                {autoEvaluating ? 'Scanning...' : 'Auto-Evaluate from Platform Data'}
              </Button>
              <div className="flex gap-2">
                {Object.entries(STATUS_COLORS).map(([k, v]) => (
                  <span key={k} className="text-[9px] flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: v.bg }} />{v.label}: {checklist?.items?.filter(i => i.status === k).length || 0}</span>
                ))}
              </div>
            </div>
          </div>
          <Progress value={cs.partial_rate || 0} className="h-2 mb-3" />
          {checklist?.items?.map((item, i) => {
            const sc = STATUS_COLORS[item.status] || STATUS_COLORS.not_started;
            return (
              <Card key={i} className="border-[#E5E7EB]" data-testid={`checklist-${item.clause}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <Badge className="text-[9px] px-1.5 py-0.5 flex-shrink-0 font-mono" style={{ backgroundColor: '#002FA7', color: 'white' }}>{item.clause}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h4 className="text-xs font-semibold text-[#0A0A0A]">{item.title}</h4>
                        <Badge className="text-[7px] px-1 py-0" style={{ backgroundColor: sc.bg + '15', color: sc.bg }}>{sc.label}</Badge>
                      </div>
                      <p className="text-[10px] text-[#4B5563]">{item.description}</p>
                      <p className="text-[9px] text-[#007AFF] mt-0.5 flex items-center gap-1"><ArrowRight className="w-2.5 h-2.5" />{item.kstreak_feature}</p>
                      {item.auto_evidence && (
                        <p className="text-[9px] mt-0.5 flex items-center gap-1" style={{ color: item.auto_status === 'compliant' ? '#34C759' : item.auto_status === 'partial' ? '#FF9500' : '#9CA3AF' }} data-testid={`auto-evidence-${item.clause}`}>
                          <CheckCircle className="w-2.5 h-2.5" />Auto: {item.auto_evidence}
                        </p>
                      )}
                    </div>
                    <Select value={item.status} onValueChange={v => updateChecklistItem(item.clause, v)}>
                      <SelectTrigger className="w-[110px] h-7 text-[10px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Not Started</SelectItem>
                        <SelectItem value="gap">Gap</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="compliant">Compliant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ==================== CONTEXT & SCOPE (4.1-4.3) ==================== */}
        <TabsContent value="context" data-testid="context-content">
          <Card className="border-[#E5E7EB]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Organization Context, Stakeholders & KMS Scope</CardTitle>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => setEditingContext(!editingContext)} data-testid="edit-context-btn">
                  {editingContext ? <Eye className="w-3 h-3 mr-1" /> : <Edit3 className="w-3 h-3 mr-1" />}{editingContext ? 'View' : 'Edit'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingContext ? (
                <>
                  <div><Label className="text-xs font-semibold">4.1 — Organization Context</Label><Textarea rows={3} value={context?.organization_context || ''} onChange={e => setContext(p => ({ ...p, organization_context: e.target.value }))} placeholder="Describe your organization's purpose, strategic direction, and how KM supports it..." className="mt-1 text-xs" data-testid="org-context-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs font-semibold">Internal Issues</Label><Textarea rows={3} value={(context?.internal_issues || []).join('\n')} onChange={e => setContext(p => ({ ...p, internal_issues: e.target.value.split('\n').filter(Boolean) }))} placeholder="One per line: culture, resources, structure..." className="mt-1 text-xs" data-testid="internal-issues-input" /></div>
                    <div><Label className="text-xs font-semibold">External Issues</Label><Textarea rows={3} value={(context?.external_issues || []).join('\n')} onChange={e => setContext(p => ({ ...p, external_issues: e.target.value.split('\n').filter(Boolean) }))} placeholder="One per line: regulations, market, technology..." className="mt-1 text-xs" data-testid="external-issues-input" /></div>
                  </div>
                  <div><Label className="text-xs font-semibold">4.2 — Stakeholders (name | need | expectation — one per line)</Label><Textarea rows={3} value={(context?.stakeholders || []).join('\n')} onChange={e => setContext(p => ({ ...p, stakeholders: e.target.value.split('\n').filter(Boolean) }))} placeholder="Senior Leadership | Strategic KM alignment | KM supports business goals&#10;Employees | Easy access to knowledge | Fast search, reliable content" className="mt-1 text-xs" data-testid="stakeholders-input" /></div>
                  <div><Label className="text-xs font-semibold">4.3 — KMS Scope Statement</Label><Textarea rows={2} value={context?.scope_statement || ''} onChange={e => setContext(p => ({ ...p, scope_statement: e.target.value }))} placeholder="Define what the KMS covers: departments, processes, knowledge types..." className="mt-1 text-xs" data-testid="scope-statement-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs font-semibold">Scope Boundaries</Label><Textarea rows={2} value={context?.scope_boundaries || ''} onChange={e => setContext(p => ({ ...p, scope_boundaries: e.target.value }))} className="mt-1 text-xs" data-testid="scope-boundaries-input" /></div>
                    <div><Label className="text-xs font-semibold">Scope Exclusions</Label><Textarea rows={2} value={context?.scope_exclusions || ''} onChange={e => setContext(p => ({ ...p, scope_exclusions: e.target.value }))} className="mt-1 text-xs" data-testid="scope-exclusions-input" /></div>
                  </div>
                  <Button onClick={saveContext} disabled={saving} data-testid="save-context-btn" className="bg-[#002FA7]">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save Context</Button>
                </>
              ) : (
                <div className="space-y-4">
                  {[
                    { label: '4.1 Organization Context', value: context?.organization_context },
                    { label: 'Internal Issues', value: (context?.internal_issues || []).map(i => `• ${i}`).join('\n') },
                    { label: 'External Issues', value: (context?.external_issues || []).map(i => `• ${i}`).join('\n') },
                    { label: '4.2 Stakeholders', value: (context?.stakeholders || []).map(i => `• ${i}`).join('\n') },
                    { label: '4.3 KMS Scope', value: context?.scope_statement },
                    { label: 'Boundaries', value: context?.scope_boundaries },
                    { label: 'Exclusions', value: context?.scope_exclusions },
                  ].map((f, i) => f.value ? (
                    <div key={i}><p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{f.label}</p><p className="text-xs text-[#4B5563] whitespace-pre-wrap mt-1">{f.value}</p></div>
                  ) : null)}
                  {!context?.organization_context && !context?.scope_statement && <p className="text-xs text-[#9CA3AF] text-center py-4">No context defined yet. Click "Edit" to begin.</p>}
                  {context?.updated_by_name && <p className="text-[9px] text-[#9CA3AF]">Last updated by {context.updated_by_name} on {context.updated_at?.substring(0, 10)}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== POLICY & LEADERSHIP (5) ==================== */}
        <TabsContent value="policy" className="space-y-4" data-testid="policy-content">
          <Card className="border-[#E5E7EB]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">KM Policy & Leadership Commitment</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => setEditingPolicy(!editingPolicy)} data-testid="edit-policy-btn">
                    {editingPolicy ? <Eye className="w-3 h-3 mr-1" /> : <Edit3 className="w-3 h-3 mr-1" />}{editingPolicy ? 'View' : 'Edit'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs text-[#AF52DE]" onClick={() => setReviewDialogOpen(true)} data-testid="add-review-btn"><Plus className="w-3 h-3 mr-1" />Management Review</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingPolicy ? (
                <>
                  <div><Label className="text-xs font-semibold">5.2 — KM Policy Statement</Label><Textarea rows={4} value={policy?.policy_statement || ''} onChange={e => setPolicy(p => ({ ...p, policy_statement: e.target.value }))} placeholder="Our organization is committed to systematically managing knowledge as a strategic asset..." className="mt-1 text-xs" data-testid="policy-statement-input" /></div>
                  <div><Label className="text-xs font-semibold">Policy Objectives</Label><Textarea rows={2} value={policy?.policy_objectives || ''} onChange={e => setPolicy(p => ({ ...p, policy_objectives: e.target.value }))} className="mt-1 text-xs" data-testid="policy-objectives-input" /></div>
                  <div><Label className="text-xs font-semibold">5.1 — Management Commitment</Label><Textarea rows={2} value={policy?.management_commitment || ''} onChange={e => setPolicy(p => ({ ...p, management_commitment: e.target.value }))} className="mt-1 text-xs" data-testid="mgmt-commitment-input" /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">Version</Label><Input value={policy?.version || ''} onChange={e => setPolicy(p => ({ ...p, version: e.target.value }))} className="mt-1 text-xs" /></div>
                    <div><Label className="text-xs">Approved By</Label><Input value={policy?.approved_by || ''} onChange={e => setPolicy(p => ({ ...p, approved_by: e.target.value }))} className="mt-1 text-xs" /></div>
                    <div><Label className="text-xs">Review Due</Label><Input type="date" value={policy?.review_due_date || ''} onChange={e => setPolicy(p => ({ ...p, review_due_date: e.target.value }))} className="mt-1 text-xs" /></div>
                  </div>
                  <div><Label className="text-xs font-semibold">5.3 — KMS Roles (name | role | responsibility — one per line)</Label><Textarea rows={3} value={(policy?.roles || []).join('\n')} onChange={e => setPolicy(p => ({ ...p, roles: e.target.value.split('\n').filter(Boolean) }))} placeholder="Jane Doe | KM Champion | Drives KM adoption across engineering&#10;John Smith | KM Sponsor | Executive oversight and resource allocation" className="mt-1 text-xs" data-testid="roles-input" /></div>
                  <Button onClick={savePolicy} disabled={saving} className="bg-[#002FA7]" data-testid="save-policy-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save Policy</Button>
                </>
              ) : (
                <div className="space-y-3">
                  {policy?.policy_statement ? <div className="p-3 rounded-lg bg-[#002FA7]/[0.03] border border-[#002FA7]/15"><p className="text-[10px] font-semibold text-[#002FA7] uppercase mb-1">KM Policy Statement</p><p className="text-xs text-[#4B5563] whitespace-pre-wrap">{policy.policy_statement}</p></div> : <p className="text-xs text-[#9CA3AF] text-center py-4">No policy defined. Click "Edit" to create one.</p>}
                  {policy?.management_commitment && <div><p className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Management Commitment</p><p className="text-xs text-[#4B5563] mt-1">{policy.management_commitment}</p></div>}
                  {policy?.roles?.length > 0 && <div><p className="text-[10px] font-semibold text-[#9CA3AF] uppercase mb-1">KMS Roles & Responsibilities</p>{policy.roles.map((r, i) => <p key={i} className="text-xs text-[#4B5563]">• {r}</p>)}</div>}
                  {policy?.approved_by && <p className="text-[9px] text-[#9CA3AF]">v{policy.version} | Approved by {policy.approved_by}{policy.review_due_date ? ` | Review due: ${policy.review_due_date}` : ''}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Management Reviews (9.3) */}
          {policy?.management_reviews?.length > 0 && (
            <Card className="border-[#E5E7EB]" data-testid="mgmt-reviews">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4 text-[#AF52DE]" />Management Reviews (Clause 9.3)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {policy.management_reviews.map((rev, i) => (
                  <div key={i} className="p-2.5 rounded-lg border border-[#E5E7EB]" data-testid={`review-${rev.review_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-[#0A0A0A]">{rev.date}</span>
                      <span className="text-[9px] text-[#9CA3AF]">by {rev.recorded_by}</span>
                    </div>
                    <p className="text-[10px] text-[#4B5563]"><b>Summary:</b> {rev.summary}</p>
                    {rev.decisions && <p className="text-[10px] text-[#4B5563]"><b>Decisions:</b> {rev.decisions}</p>}
                    {rev.action_items && <p className="text-[10px] text-[#4B5563]"><b>Actions:</b> {rev.action_items}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== RISK REGISTER (6.1) ==================== */}
        <TabsContent value="risks" data-testid="risks-content">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#4B5563]">Clause 6.1 — Risks and opportunities affecting the KMS</p>
            <Button size="sm" className="bg-[#002FA7] text-xs" onClick={() => setRiskDialogOpen(true)} data-testid="add-risk-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Risk / Opportunity</Button>
          </div>
          <div className="space-y-2">
            {risks.length === 0 ? <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF] text-sm">No risks or opportunities registered yet.</CardContent></Card> :
              risks.map(r => {
                const scoreColor = r.risk_score >= 12 ? '#FF3B30' : r.risk_score >= 6 ? '#FF9500' : '#34C759';
                return (
                  <Card key={r.risk_id} className="border-[#E5E7EB]" data-testid={`risk-${r.risk_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Badge className="text-[8px] px-1" style={{ backgroundColor: r.type === 'opportunity' ? '#34C75915' : '#FF3B3015', color: r.type === 'opportunity' ? '#34C759' : '#FF3B30' }}>{r.type}</Badge>
                            <h4 className="text-xs font-semibold">{r.title}</h4>
                            {r.iso_clause && <Badge variant="outline" className="text-[8px] px-1 font-mono">{r.iso_clause}</Badge>}
                          </div>
                          <p className="text-[10px] text-[#4B5563]">{r.description}</p>
                          <div className="flex items-center gap-3 mt-1 text-[9px] text-[#9CA3AF]">
                            <span>L: {r.likelihood}</span><span>I: {r.impact}</span>
                            <span className="font-bold" style={{ color: scoreColor }}>Score: {r.risk_score}</span>
                            {r.owner && <span>Owner: {r.owner}</span>}
                          </div>
                          {r.mitigation_plan && <p className="text-[9px] text-[#007AFF] mt-0.5">Mitigation: {r.mitigation_plan}</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Select value={r.status} onValueChange={v => updateRiskStatus(r.risk_id, v)}>
                            <SelectTrigger className="w-[90px] h-6 text-[9px]"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="mitigated">Mitigated</SelectItem><SelectItem value="accepted">Accepted</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
                          </Select>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-[#FF3B30]" onClick={() => deleteRisk(r.risk_id)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            }
          </div>
        </TabsContent>

        {/* ==================== KM OBJECTIVES (6.2) ==================== */}
        <TabsContent value="objectives" data-testid="objectives-content">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#4B5563]">Clause 6.2 — SMART KM objectives with measurable targets</p>
            <Button size="sm" className="bg-[#002FA7] text-xs" onClick={() => setObjDialogOpen(true)} data-testid="add-objective-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Objective</Button>
          </div>
          <div className="space-y-2">
            {objectives.length === 0 ? <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF] text-sm">No KM objectives defined yet.</CardContent></Card> :
              objectives.map(o => {
                const pct = o.target_value && o.current_value ? Math.min(100, Math.round((parseFloat(o.current_value) / parseFloat(o.target_value)) * 100)) : 0;
                const stColor = o.status === 'achieved' ? '#34C759' : o.status === 'at_risk' ? '#FF3B30' : '#007AFF';
                return (
                  <Card key={o.objective_id} className="border-[#E5E7EB]" data-testid={`objective-${o.objective_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-xs font-semibold">{o.title}</h4>
                            {o.iso_clause && <Badge variant="outline" className="text-[8px] px-1 font-mono">{o.iso_clause}</Badge>}
                          </div>
                          {o.description && <p className="text-[10px] text-[#4B5563] mb-1">{o.description}</p>}
                          <div className="flex items-center gap-2 mb-1">
                            <Progress value={pct} className="flex-1 h-1.5" />
                            <span className="text-[10px] font-bold" style={{ color: stColor }}>{o.current_value || '0'}/{o.target_value || '?'}{o.unit}</span>
                          </div>
                          <div className="flex items-center gap-3 text-[9px] text-[#9CA3AF]">
                            {o.metric && <span>Metric: {o.metric}</span>}
                            {o.owner && <span>Owner: {o.owner}</span>}
                            {o.target_date && <span>Due: {o.target_date}</span>}
                          </div>
                        </div>
                        <Select value={o.status} onValueChange={v => updateObjectiveStatus(o.objective_id, v)}>
                          <SelectTrigger className="w-[100px] h-6 text-[9px]"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="achieved">Achieved</SelectItem><SelectItem value="at_risk">At Risk</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            }
          </div>
        </TabsContent>

        {/* ==================== DOCUMENT CONTROL (7.5) ==================== */}
        <TabsContent value="documents" data-testid="documents-content">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#4B5563]">Clause 7.5 — Version-controlled policy & procedure register</p>
            <Button size="sm" className="bg-[#002FA7] text-xs" onClick={() => setDocDialogOpen(true)} data-testid="add-document-btn"><Plus className="w-3.5 h-3.5 mr-1" />Register Document</Button>
          </div>
          <div className="space-y-2">
            {documents.length === 0 ? <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF] text-sm">No controlled documents registered yet.</CardContent></Card> :
              documents.map(d => (
                <Card key={d.doc_id} className="border-[#E5E7EB]" data-testid={`document-${d.doc_id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#002FA7]" />
                        <div>
                          <h4 className="text-xs font-semibold">{d.title}</h4>
                          <div className="flex items-center gap-2 text-[9px] text-[#9CA3AF]">
                            <span>v{d.version}</span>
                            <Badge className="text-[7px]" style={{ backgroundColor: d.status === 'approved' ? '#34C75915' : d.status === 'draft' ? '#FF950015' : '#9CA3AF15', color: d.status === 'approved' ? '#34C759' : d.status === 'draft' ? '#FF9500' : '#9CA3AF' }}>{d.status}</Badge>
                            <span>{d.doc_type}</span>
                            {d.owner && <span>Owner: {d.owner}</span>}
                            {d.iso_clause && <Badge variant="outline" className="text-[7px] px-1 font-mono">{d.iso_clause}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[9px] text-[#9CA3AF]">
                        {d.change_history?.length > 0 && <span>{d.change_history.length} revision(s)</span>}
                        <span>{d.updated_at?.substring(0, 10)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            }
          </div>
        </TabsContent>
      </Tabs>

      {/* ==================== DIALOGS ==================== */}
      <Dialog open={riskDialogOpen} onOpenChange={setRiskDialogOpen}>
        <DialogContent data-testid="risk-dialog"><DialogHeader><DialogTitle>Add Risk / Opportunity</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type</Label><Select value={riskForm.type} onValueChange={v => setRiskForm(p => ({ ...p, type: v }))}><SelectTrigger className="text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="risk">Risk</SelectItem><SelectItem value="opportunity">Opportunity</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Category</Label><Select value={riskForm.category} onValueChange={v => setRiskForm(p => ({ ...p, category: v }))}><SelectTrigger className="text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="operational">Operational</SelectItem><SelectItem value="strategic">Strategic</SelectItem><SelectItem value="cultural">Cultural</SelectItem><SelectItem value="technology">Technology</SelectItem><SelectItem value="compliance">Compliance</SelectItem></SelectContent></Select></div>
            </div>
            <div><Label className="text-xs">Title *</Label><Input value={riskForm.title} onChange={e => setRiskForm(p => ({ ...p, title: e.target.value }))} className="text-xs" data-testid="risk-title-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea rows={2} value={riskForm.description} onChange={e => setRiskForm(p => ({ ...p, description: e.target.value }))} className="text-xs" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Likelihood</Label><Select value={riskForm.likelihood} onValueChange={v => setRiskForm(p => ({ ...p, likelihood: v }))}><SelectTrigger className="text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="very_low">Very Low</SelectItem><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="very_high">Very High</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Impact</Label><Select value={riskForm.impact} onValueChange={v => setRiskForm(p => ({ ...p, impact: v }))}><SelectTrigger className="text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="very_low">Very Low</SelectItem><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="very_high">Very High</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">ISO Clause</Label><Input value={riskForm.iso_clause} onChange={e => setRiskForm(p => ({ ...p, iso_clause: e.target.value }))} placeholder="e.g., 4.5" className="text-xs" /></div>
            </div>
            <div><Label className="text-xs">Owner</Label><Input value={riskForm.owner} onChange={e => setRiskForm(p => ({ ...p, owner: e.target.value }))} className="text-xs" /></div>
            <div><Label className="text-xs">Mitigation Plan</Label><Textarea rows={2} value={riskForm.mitigation_plan} onChange={e => setRiskForm(p => ({ ...p, mitigation_plan: e.target.value }))} className="text-xs" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRiskDialogOpen(false)}>Cancel</Button><Button className="bg-[#002FA7]" onClick={createRisk} data-testid="submit-risk-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={objDialogOpen} onOpenChange={setObjDialogOpen}>
        <DialogContent data-testid="objective-dialog"><DialogHeader><DialogTitle>Add KM Objective</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title *</Label><Input value={objForm.title} onChange={e => setObjForm(p => ({ ...p, title: e.target.value }))} className="text-xs" data-testid="obj-title-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea rows={2} value={objForm.description} onChange={e => setObjForm(p => ({ ...p, description: e.target.value }))} className="text-xs" /></div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label className="text-xs">Metric</Label><Input value={objForm.metric} onChange={e => setObjForm(p => ({ ...p, metric: e.target.value }))} placeholder="e.g., Task completion" className="text-xs" /></div>
              <div><Label className="text-xs">Target</Label><Input value={objForm.target_value} onChange={e => setObjForm(p => ({ ...p, target_value: e.target.value }))} className="text-xs" /></div>
              <div><Label className="text-xs">Current</Label><Input value={objForm.current_value} onChange={e => setObjForm(p => ({ ...p, current_value: e.target.value }))} className="text-xs" /></div>
              <div><Label className="text-xs">Unit</Label><Input value={objForm.unit} onChange={e => setObjForm(p => ({ ...p, unit: e.target.value }))} className="text-xs" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Owner</Label><Input value={objForm.owner} onChange={e => setObjForm(p => ({ ...p, owner: e.target.value }))} className="text-xs" /></div>
              <div><Label className="text-xs">Target Date</Label><Input type="date" value={objForm.target_date} onChange={e => setObjForm(p => ({ ...p, target_date: e.target.value }))} className="text-xs" /></div>
              <div><Label className="text-xs">ISO Clause</Label><Input value={objForm.iso_clause} onChange={e => setObjForm(p => ({ ...p, iso_clause: e.target.value }))} placeholder="e.g., 6.2" className="text-xs" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setObjDialogOpen(false)}>Cancel</Button><Button className="bg-[#002FA7]" onClick={createObjective} data-testid="submit-obj-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent data-testid="document-dialog"><DialogHeader><DialogTitle>Register Controlled Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title *</Label><Input value={docForm.title} onChange={e => setDocForm(p => ({ ...p, title: e.target.value }))} className="text-xs" data-testid="doc-title-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Type</Label><Select value={docForm.doc_type} onValueChange={v => setDocForm(p => ({ ...p, doc_type: v }))}><SelectTrigger className="text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="policy">Policy</SelectItem><SelectItem value="procedure">Procedure</SelectItem><SelectItem value="guideline">Guideline</SelectItem><SelectItem value="form">Form/Template</SelectItem><SelectItem value="record">Record</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Version</Label><Input value={docForm.version} onChange={e => setDocForm(p => ({ ...p, version: e.target.value }))} className="text-xs" /></div>
              <div><Label className="text-xs">ISO Clause</Label><Input value={docForm.iso_clause} onChange={e => setDocForm(p => ({ ...p, iso_clause: e.target.value }))} className="text-xs" /></div>
            </div>
            <div><Label className="text-xs">Owner</Label><Input value={docForm.owner} onChange={e => setDocForm(p => ({ ...p, owner: e.target.value }))} className="text-xs" /></div>
            <div><Label className="text-xs">Description</Label><Textarea rows={2} value={docForm.description} onChange={e => setDocForm(p => ({ ...p, description: e.target.value }))} className="text-xs" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDocDialogOpen(false)}>Cancel</Button><Button className="bg-[#002FA7]" onClick={createDocument} data-testid="submit-doc-btn">Register</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent data-testid="review-dialog"><DialogHeader><DialogTitle>Record Management Review (Clause 9.3)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Review Date</Label><Input type="date" value={reviewForm.date} onChange={e => setReviewForm(p => ({ ...p, date: e.target.value }))} className="text-xs" data-testid="review-date-input" /></div>
              <div><Label className="text-xs">Next Review Date</Label><Input type="date" value={reviewForm.next_review_date} onChange={e => setReviewForm(p => ({ ...p, next_review_date: e.target.value }))} className="text-xs" /></div>
            </div>
            <div><Label className="text-xs">Attendees</Label><Input value={reviewForm.attendees} onChange={e => setReviewForm(p => ({ ...p, attendees: e.target.value }))} placeholder="Names separated by commas" className="text-xs" /></div>
            <div><Label className="text-xs">Summary</Label><Textarea rows={2} value={reviewForm.summary} onChange={e => setReviewForm(p => ({ ...p, summary: e.target.value }))} className="text-xs" data-testid="review-summary-input" /></div>
            <div><Label className="text-xs">Decisions</Label><Textarea rows={2} value={reviewForm.decisions} onChange={e => setReviewForm(p => ({ ...p, decisions: e.target.value }))} className="text-xs" /></div>
            <div><Label className="text-xs">Action Items</Label><Textarea rows={2} value={reviewForm.action_items} onChange={e => setReviewForm(p => ({ ...p, action_items: e.target.value }))} className="text-xs" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setReviewDialogOpen(false)}>Cancel</Button><Button className="bg-[#002FA7]" onClick={addReview} data-testid="submit-review-btn">Record</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompliancePage;
